import { XIcon } from "lucide-react";
import { motion, useReducedMotion } from "motion/react";
import type * as React from "react";
import { useCallback, useRef, useState } from "react";
import { flushSync } from "react-dom";

import { Badge } from "@/web/components/primitives/badge";
import { Button, type ButtonProps } from "@/web/components/primitives/button";
import { cn } from "@/web/lib/styling";
import { CATEGORY_PALETTES } from "@/web/routes/_protected/smb/-dashboard/-categoryPalettes";
import type {
  SmbActionCategory,
  SmbActionCoreProps,
  SmbActionDefinition,
} from "@/web/routes/_protected/smb/-dashboard/-types";

/**
 * The completed-card flavor: badge + title only, plus any combination of an
 * optional back face (Details flip) and an optional action button. The visual
 * is materially simpler than `<ActionCard />` — no drawer, no swipe, no body
 * region, no separate sub-components — so the API is a single props bag.
 *
 * When both `details` and `action` are present, the front face stacks the
 * Details trigger above the action button. Sized for a 2-up wrap grid; the
 * active card's full-width portrait would dwarf neighbors at this density.
 */
type CompletedActionCardSurface = {
  category: SmbActionCategory;
  badge: string;
  title: string;
  className?: string;
  details?: { title: string; children: React.ReactNode };
  action?: { label: string; onClick: () => void };
  /** Stable, copy-independent hook for e2e to assert a specific completed card. */
  testId?: string;
};

export function CompletedActionCard(props: CompletedActionCardSurface) {
  const { category, badge, title, details, action, className, testId } = props;
  const palette = CATEGORY_PALETTES[category];
  const reduceMotion = useReducedMotion();

  const [isFlipped, setIsFlipped] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);

  // Mirrors -ActionCard.tsx: flushSync the state so `inert` commits before
  // we move focus, otherwise `.focus()` no-ops on a still-inert element.
  const flipToBack = useCallback(() => {
    flushSync(() => setIsFlipped(true));
    closeRef.current?.focus();
  }, []);
  const flipToFront = useCallback(() => {
    const shouldRestoreFocus = document.activeElement === closeRef.current;
    flushSync(() => setIsFlipped(false));
    if (shouldRestoreFocus) triggerRef.current?.focus();
  }, []);

  const faceClass = cn(
    "flex aspect-7/10 w-full flex-col gap-3 p-4 [grid-area:1/1]",
    "overflow-hidden border border-withus-dark-blue shadow-withus-default backface-hidden",
    palette.cardBg,
    palette.onCardFg,
  );

  return (
    <article
      data-slot="smb-completed-action-card"
      data-category={category}
      data-testid={testId}
      className={cn("w-full perspective-distant", className)}
    >
      <motion.div
        className="grid transform-3d"
        initial={false}
        animate={{ rotateY: isFlipped ? 180 : 0 }}
        transition={{ type: "tween", duration: reduceMotion ? 0 : 0.5, ease: "easeOut" }}
      >
        {/* Front face */}
        <div
          data-slot="smb-completed-action-card-front"
          className={cn(faceClass, "justify-between")}
          aria-hidden={isFlipped}
          inert={isFlipped}
        >
          <div className="flex flex-col gap-3">
            <Badge
              variant="ghost"
              size="sm"
              className={cn(
                "w-fit text-[0.6875rem] leading-none tracking-wider",
                palette.badgeClass,
              )}
            >
              {badge}
            </Badge>
            <h3
              className="line-clamp-3 font-heading text-sm leading-tight text-ellipsis uppercase"
              title={title}
            >
              {title}
            </h3>
          </div>
          {details || action ? (
            <div className="flex flex-col gap-2">
              {details ? (
                <DetailsButton ref={triggerRef} palette={palette} onClick={flipToBack} />
              ) : null}
              {action ? (
                <ActionButton palette={palette} label={action.label} onClick={action.onClick} />
              ) : null}
            </div>
          ) : null}
        </div>
        {/* Back face — only mounted when details are provided */}
        {details ? (
          <div
            data-slot="smb-completed-action-card-back"
            className={cn(faceClass, "rotate-y-180")}
            aria-hidden={!isFlipped}
            inert={!isFlipped}
          >
            <header className="flex w-full items-start justify-between gap-2">
              <h3 className="withus-strong text-sm leading-tight">{details.title}</h3>
              <Button
                ref={closeRef}
                variant="ghost"
                size="icon"
                onClick={flipToFront}
                aria-label="Close details"
              >
                <XIcon aria-hidden />
              </Button>
            </header>
            <div className="flex-1 overflow-y-auto text-sm leading-snug">{details.children}</div>
          </div>
        ) : null}
      </motion.div>
    </article>
  );
}

function DetailsButton({
  ref,
  palette,
  onClick,
}: {
  ref: React.Ref<HTMLButtonElement>;
  palette: (typeof CATEGORY_PALETTES)[SmbActionCategory];
  onClick: () => void;
}) {
  return (
    <Button
      ref={ref}
      variant="solid"
      color={palette.primaryButtonColor as NonNullable<ButtonProps["color"]>}
      size="md"
      onClick={onClick}
      className={cn("w-full", palette.primaryButtonClass)}
    >
      Details
    </Button>
  );
}

function ActionButton({
  palette,
  label,
  onClick,
}: {
  palette: (typeof CATEGORY_PALETTES)[SmbActionCategory];
  label: string;
  onClick: () => void;
}) {
  return (
    <Button
      variant="solid"
      color={palette.primaryButtonColor as NonNullable<ButtonProps["color"]>}
      size="md"
      onClick={onClick}
      className={cn("w-full", palette.primaryButtonClass)}
    >
      {label}
    </Button>
  );
}

/**
 * Generic placeholder factory for completed cards that don't have a bespoke
 * surface yet. Renders a no-CTA card. Replace per-rule with bespoke factories
 * (see `-completedActions/`) as the visuals get fleshed out.
 */
export function createCompletedActionCard(props: {
  id: string;
  category: SmbActionCategory;
  badge: string;
  title: string;
}): SmbActionDefinition {
  const { id, category, badge, title } = props;
  return {
    id,
    category,
    canSkip: false,
    render: (_core: SmbActionCoreProps) => (
      <CompletedActionCard category={category} badge={badge} title={title} />
    ),
  };
}
